import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { VisitAttributes } from '@albatrosdeveloper/ave-models-package';
import { DocumentNavigationComponent } from 'app/common/components/document-navigation/document-navigation.component';
import { UnifiedOrderBadgeComponent } from 'app/shared/components/unified-order-badge/unified-order-badge.component';

/**
 * Document Navigation Cell Renderer
 *
 * Wraps the DocumentNavigationComponent for use in smart-grid tables.
 * Displays a navigable link to related documents (orders, deliveries, etc.)
 * Handles empty/null serials correctly by showing '-' instead.
 * Includes UnifiedOrderBadge for unified orders.
 *
 * @example
 * ```typescript
 * {
 *   field: 'serials',
 *   headerName: 'Documento',
 *   cellRenderer: DocumentNavigationCellRendererComponent,
 *   minWidth: 150
 * }
 * ```
 */
@Component({
  selector: 'app-document-navigation-cell-renderer',
  standalone: true,
  imports: [CommonModule, DocumentNavigationComponent, UnifiedOrderBadgeComponent],
  template: `
    <div class="flex items-center gap-2">
      <app-document-navigation
        [documentType]="documentType"
        [documentSerial]="documentSerial"
        [displayText]="displayText"
        [showIcon]="showIcon">
      </app-document-navigation>

      <app-unified-order-badge
        *ngIf="hasSourceTransactions"
        [order]="orderData">
      </app-unified-order-badge>
    </div>
  `,
})
export class DocumentNavigationCellRendererComponent implements CellRenderer<VisitAttributes> {
  @Input() params!: CellRendererParams<VisitAttributes>;

  get documentType(): string {
    // Try documentType first, fallback to transactionType code
    // @ts-ignore
    return this.params?.data?.type?.code ?? this.params.data?.transactionType?.code ?? '';
  }

  get documentSerial(): string | undefined {
    return this.params.data?.serials;
  }

  get displayText(): string {
    const serials = this.params.data?.serials;
    return (serials && serials.trim() !== '' && serials !== 'null') ? serials : '-';
  }

  get showIcon(): boolean {
    const serials = this.params.data?.serials;
    return !!(serials && serials.trim() !== '' && serials !== 'null');
  }

  /**
   * Checks if the data has source transactions
   */
  get hasSourceTransactions(): boolean {
    // @ts-ignore - sourceTransactions may exist on extended data
    return !!(this.params.data?.sourceTransactions?.length);
  }

  /**
   * Order data for the unified order badge
   */
  get orderData(): any {
    return {
      serial: this.documentSerial,
      // @ts-ignore - sourceTransactions may exist on extended data
      sourceTransactions: this.params.data?.sourceTransactions,
      // @ts-ignore - operationType may exist on extended data
      operationType: this.params.data?.operationType,
      // @ts-ignore - isUnified may exist on extended data
      isUnified: this.params.data?.isUnified
    };
  }
}
